import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { api } from '~/api';
import { Order } from '~/models';

export const useOrder = (orderId?: string, warehouse?: { id: string }) => {
  const isdao = useStoreState((state: any) => state.auth.isdao);
  const setOrderQuestions = useStoreActions(
    (action: any) => action.marketdepth.setOrderQuestions
  );

  // Derive warehouseId based on DAO mode for consistent queryKey and params
  const warehouseId = !isdao ? warehouse?.id : undefined;

  const query = useQuery({
    queryKey: ['orders', orderId, warehouseId],
    queryFn: async () => {
      if (!orderId) return null;

      const params: any = warehouseId ? { warehouse: warehouseId } : {};

      return api.getData(
        {
          id: orderId,
          type: 'orders',
          getResult: (x: any) => new Order(x.order),
        },
        params
      );
    },
    enabled: !!orderId,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });

  // Sync order questions to store when data changes
  useEffect(() => {
    if (query.data && orderId) {
      setOrderQuestions({
        id: orderId,
        questions: query.data.questions || undefined,
      });
    }
  }, [query.data, orderId, setOrderQuestions]);

  return query;
};

export const useNewOrderSchema = (orderId?: string) => {
  return useQuery({
    queryKey: ['newOrderSchema', orderId],
    queryFn: async () => {
      if (!orderId) return null;

      const schema = await api.getData(
        { type: 'orders', id: 'neworder' },
        { 'fok-order-id': orderId }
      );

      return schema;
    },
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000, // 5 minutes - schema doesn't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
